import { useId, useState, type ReactNode } from 'react';
import type { NonConformityRow } from '@obraplan/core/application/use-cases/quality/non-conformity-use-cases';
import type { Attachment } from '@obraplan/core/domain/attachments/attachment';
import type { AttachmentUsage } from '@obraplan/core/application/use-cases/planning/attachment-use-cases';
import {
  MAX_NON_CONFORMITY_CANCEL_REASON,
  MAX_NON_CONFORMITY_SOLUTION,
  MIN_NON_CONFORMITY_CANCEL_REASON,
  MIN_NON_CONFORMITY_SOLUTION,
} from '@obraplan/core/domain/quality/non-conformity';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/input';
import { AttachmentGallery } from '@/features/planning/AttachmentGallery';
import { formatBusinessDate, formatShortDate } from '@/lib/format';
import { formatNcQuantity } from './non-conformity-list';

/** O dia de um instante gravado, como a tela fala: `22/10/2026`. */
function diaDoInstante(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

export interface NonConformityPhotosState {
  readonly attachments: readonly Attachment[];
  readonly usage: AttachmentUsage | undefined;
}

export interface NonConformityDetailProps {
  readonly row: NonConformityRow;
  /** Quem tem a aba encerra, cancela e põe foto (D8). */
  readonly canManage: boolean;
  /** Tirar foto é da conferência (`labor.modify`), como em toda galeria. */
  readonly canRemovePhoto: boolean;
  readonly photos: NonConformityPhotosState | undefined;
  readonly isPending: boolean;
  readonly resolveUrl: (attachmentId: string) => Promise<string | undefined>;
  readonly onResolve: (solution: string) => void;
  readonly onCancel: (reason: string) => void;
  readonly onAttach: (files: readonly File[]) => void;
  readonly onRemovePhoto: (attachmentId: string) => void;
}

/**
 * O detalhe de uma NC, aberto ao clicar na linha (Fase 37, etapa 2).
 *
 * Tudo o que a lista resume, por extenso, e o que se faz com ela: a aberta se encerra com a
 * solução (RN-482) ou se cancela com o motivo (RN-483); as fotos entram em qualquer uma que
 * não foi cancelada (RN-485). A resolvida e a cancelada dizem quem, quando e — a cancelada —
 * por quê.
 */
export function NonConformityDetail({
  row,
  canManage,
  canRemovePhoto,
  photos,
  isPending,
  resolveUrl,
  onResolve,
  onCancel,
  onAttach,
  onRemovePhoto,
}: NonConformityDetailProps) {
  const id = useId();
  const [solution, setSolution] = useState('');
  const [cancelando, setCancelando] = useState(false);
  const [reason, setReason] = useState('');
  const quantidade = formatNcQuantity(row);
  const aberta = row.status === 'OPEN';

  return (
    <div className="grid gap-5 border-t border-(--color-border-subtle) bg-(--color-surface-sunken) px-4 py-4 md:grid-cols-[minmax(0,1fr)_minmax(16rem,20rem)] md:px-5">
      <div className="flex min-w-0 flex-col gap-4">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
          <Dado termo="Identificada em">{formatBusinessDate(row.date)}</Dado>
          <Dado termo="Medição de">{row.ownerName ?? '—'}</Dado>
          <Dado termo="Aberta por">{row.openedByName}</Dado>
          <Dado termo="Checklist">{row.checklistName}</Dado>
          <Dado termo="Item">{row.itemLabel}</Dado>
          <Dado termo="Quantidade não conforme">{quantidade ?? 'Não informada'}</Dado>
          {row.places.length > 0 && (
            <div className="col-span-2 flex min-w-0 flex-col gap-0.5 sm:col-span-3">
              <dt className="text-xs text-(--color-content-muted)">Onde</dt>
              <dd className="break-words">{row.places.map((place) => place.label).join(', ')}</dd>
            </div>
          )}
        </dl>

        {quantidade !== undefined && row.status !== 'CANCELLED' && (
          <p className="text-xs text-(--color-content-muted)">
            Os {quantidade} estão fora da produção de {formatShortDate(row.date)}. Encerrar não os
            devolve: o retrabalho entra quando for medido de novo, no dia em que for refeito.
          </p>
        )}

        {row.status === 'RESOLVED' && (
          <section aria-label="Solução" className="flex flex-col gap-1">
            <h3 className="text-sm font-medium">Solução aplicada</h3>
            <p className="text-sm whitespace-pre-line">{row.solution ?? '—'}</p>
            {row.resolvedAt !== undefined && (
              <p className="text-xs text-(--color-content-muted)">
                Resolvida por {row.resolvedByName ?? '—'} em {diaDoInstante(row.resolvedAt)}.
              </p>
            )}
          </section>
        )}

        {row.status === 'CANCELLED' && (
          <section aria-label="Cancelamento" className="flex flex-col gap-1">
            <h3 className="text-sm font-medium">Cancelada</h3>
            <p className="text-sm">
              {row.cancelReason ??
                'Cancelada por quem mediu, ao responder a checklist de novo sem reprovar o item.'}
            </p>
            {row.cancelledAt !== undefined && (
              <p className="text-xs text-(--color-content-muted)">
                Por {row.cancelledByName ?? '—'} em {diaDoInstante(row.cancelledAt)}. O número{' '}
                {row.number} não volta.
              </p>
            )}
          </section>
        )}

        {aberta && canManage && !cancelando && (
          <form
            className="flex flex-col gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              onResolve(solution);
            }}
          >
            <label htmlFor={`${id}-solucao`} className="text-sm font-medium">
              Solução aplicada
            </label>
            <Textarea
              id={`${id}-solucao`}
              rows={3}
              maxLength={MAX_NON_CONFORMITY_SOLUTION}
              placeholder="O que foi feito para corrigir."
              value={solution}
              disabled={isPending}
              onChange={(event) => setSolution(event.target.value)}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="submit"
                variant="primary"
                disabled={isPending || solution.trim().length < MIN_NON_CONFORMITY_SOLUTION}
              >
                Encerrar com a solução
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={isPending}
                onClick={() => setCancelando(true)}
              >
                Cancelar esta não conformidade
              </Button>
            </div>
          </form>
        )}

        {aberta && canManage && cancelando && (
          <form
            className="flex flex-col gap-2 rounded-md border border-(--color-state-danger) bg-(--color-surface) p-3"
            onSubmit={(event) => {
              event.preventDefault();
              onCancel(reason);
            }}
          >
            <label htmlFor={`${id}-motivo`} className="text-sm font-medium">
              Por que cancelar a nº {row.number}?
            </label>
            <Textarea
              id={`${id}-motivo`}
              rows={2}
              maxLength={MAX_NON_CONFORMITY_CANCEL_REASON}
              placeholder="Aberta em duplicidade, item errado…"
              value={reason}
              disabled={isPending}
              onChange={(event) => setReason(event.target.value)}
            />
            <p className="text-xs text-(--color-content-muted)">
              É para a não conformidade aberta por engano.{' '}
              {quantidade === undefined
                ? ''
                : `Os ${quantidade} voltam à produção de ${formatShortDate(row.date)}. `}
              O item deixa de estar reprovado na ficha do dia, a menos que outra medição o tenha
              reprovado também.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="submit"
                variant="danger"
                disabled={isPending || reason.trim().length < MIN_NON_CONFORMITY_CANCEL_REASON}
              >
                Cancelar a não conformidade
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={isPending}
                onClick={() => {
                  setCancelando(false);
                  setReason('');
                }}
              >
                Voltar
              </Button>
            </div>
          </form>
        )}
      </div>

      <section aria-label={`Fotos da NC nº ${String(row.number)}`} className="flex flex-col gap-2">
        <h3 className="text-sm font-medium">Fotos</h3>
        {photos === undefined ? (
          <p className="text-xs text-(--color-content-muted)">Carregando as fotos…</p>
        ) : (
          <AttachmentGallery
            attachments={photos.attachments}
            usage={photos.usage}
            canAttach={canManage && row.status !== 'CANCELLED'}
            canRemove={canRemovePhoto}
            isPending={isPending}
            resolveUrl={resolveUrl}
            onAttach={onAttach}
            onRemove={onRemovePhoto}
          />
        )}
      </section>
    </div>
  );
}

function Dado({ termo, children }: { readonly termo: string; readonly children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-xs text-(--color-content-muted)">{termo}</dt>
      <dd className="break-words">{children}</dd>
    </div>
  );
}
